import { useCallback, useEffect, useState } from 'react';
import {
  ReactFlow,
  ReactFlowProvider,
  applyEdgeChanges,
  applyNodeChanges,
  useReactFlow,
  Background,
  Controls,
  type Connection,
  type Edge,
  type EdgeChange,
  type EdgeTypes,
  type IsValidConnection,
  type Node,
  type NodeChange,
  type NodeTypes
} from '@xyflow/react';
import '@xyflow/react/dist/style.css';

interface TreeFlowCanvasProps {
  nodes: Node[];
  edges: Edge[];
  nodeTypes: NodeTypes;
  edgeTypes: EdgeTypes;
  defaultEdgeType: string;
  nodesConnectable: boolean;
  isValidConnection: IsValidConnection;
  onConnect: (connection: Connection) => void;
  className?: string;
  /** Type MIME accepté en drop externe (ex: depuis une sidebar) — absent = drop désactivé. */
  externalDragType?: string;
  /** Appelé au drop, avec la position convertie en coordonnées du canvas (pan/zoom pris en compte). */
  onExternalDrop?: (value: string, position: { x: number; y: number }) => void;
}

/** Canvas React Flow interne : synchronise le state contrôlé nodes/edges. */
function TreeFlowCanvasInner({
  nodes: initialNodes,
  edges: initialEdges,
  nodeTypes,
  edgeTypes,
  defaultEdgeType,
  nodesConnectable,
  isValidConnection,
  onConnect,
  externalDragType,
  onExternalDrop
}: TreeFlowCanvasProps) {
  const [nodes, setNodes] = useState<Node[]>(initialNodes);
  const [edges, setEdges] = useState<Edge[]>(initialEdges);
  const { screenToFlowPosition } = useReactFlow();

  /** Resynchronise le canvas à chaque rechargement des données depuis l'appelant. */
  useEffect(() => {
    setNodes(initialNodes);
  }, [initialNodes]);

  useEffect(() => {
    setEdges(initialEdges);
  }, [initialEdges]);

  const onNodesChange = useCallback(
    (changes: NodeChange[]) => setNodes((snapshot) => applyNodeChanges(changes, snapshot)),
    []
  );

  const onEdgesChange = useCallback(
    (changes: EdgeChange[]) => setEdges((snapshot) => applyEdgeChanges(changes, snapshot)),
    []
  );

  const handleDragOver = (event: React.DragEvent) => {
    if (!externalDragType) return;
    event.preventDefault();
    event.dataTransfer.dropEffect = 'move';
  };

  const handleDrop = (event: React.DragEvent) => {
    if (!externalDragType || !onExternalDrop) return;
    const value = event.dataTransfer.getData(externalDragType);
    if (!value) return;
    event.preventDefault();
    onExternalDrop(value, screenToFlowPosition({ x: event.clientX, y: event.clientY }));
  };

  return (
    // biome-ignore lint/a11y/noStaticElementInteractions: zone de drop du drag & drop natif (HTML5 DnD), pas d'équivalent ARIA pertinent.
    <div className="size-full" onDragOver={handleDragOver} onDrop={handleDrop}>
      <ReactFlow
        nodes={nodes}
        edges={edges}
        nodeTypes={nodeTypes}
        edgeTypes={edgeTypes}
        defaultEdgeOptions={{ type: defaultEdgeType }}
        nodesConnectable={nodesConnectable}
        onNodesChange={onNodesChange}
        onEdgesChange={onEdgesChange}
        onConnect={onConnect}
        isValidConnection={isValidConnection}
        fitView
        fitViewOptions={{ padding: 0.4 }}
      >
        <Background color="hsl(var(--muted-foreground) / 0.3)" gap={20} size={1.5} />
        <Controls />
      </ReactFlow>
    </div>
  );
}

/**
 * Canvas React Flow générique pour visualiser/éditer un arbre : wrapper
 * `ReactFlowProvider` + container dimensionné. Mutualisé entre les
 * catégories produit et la hiérarchie commerciale — aucune logique métier
 * ici, tout est passé en props par le domaine appelant.
 */
export function TreeFlowCanvas({ className, ...props }: TreeFlowCanvasProps) {
  return (
    <div
      className={
        className ?? 'h-[28rem] w-full rounded-xl border bg-[hsl(293_6%_90%)] overflow-hidden'
      }
    >
      {/* Anime le repositionnement des nœuds (ajout/suppression, refetch)
          plutôt qu'un saut brut vers leur nouvelle position. */}
      <style>{`.react-flow__node { transition: transform 300ms ease-out; }`}</style>
      <ReactFlowProvider>
        <TreeFlowCanvasInner {...props} />
      </ReactFlowProvider>
    </div>
  );
}
